import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { isAiStudioEnabledByTenant } from '@ff-os/pdp-shared-redux/slices/tenantSlice';
import { Clip } from '@ff-os/rendering-shared/Clip';
import { Accordion } from '@ff-os/shared-react/components/accordion/Accordion';
import { AccordionField } from '@ff-os/shared-react/components/accordion/partials/AccordionField';
import { AccordionMenu } from '@ff-os/shared-react/components/accordion/partials/AccordionMenu';
import { AiStudioButton } from '@ff-os/shared-react/components/ai-studio/AiStudioButton';
import { AiStudioWindow } from '@ff-os/shared-react/components/ai-studio/AiStudioWindow';
import { Alert } from '@ff-os/shared-react/components/alert/Alert';
import { AspectRatio } from '@ff-os/shared-react/components/aspect-ratio/AspectRatio';
import { DropArea } from '@ff-os/shared-react/components/file-uploader/DropArea';
import { createFileUploaderRef, UploadError } from '@ff-os/shared-react/components/file-uploader/FileUploader';
import { Icon } from '@ff-os/shared-react/components/icons/Icon';
import { Spinner } from '@ff-os/shared-react/components/loaders/spinner/Spinner';
import { PhotoEditModal } from '@ff-os/shared-react/components/photo-edit/photo-edit-modal/PhotoEditModal';
import { UploadMethodsWindow } from '@ff-os/shared-react/components/upload-methods/UploadMethodsWindow';
import { WindowStackItem } from '@ff-os/shared-react/context/window-stack/WindowStackItem';
import { useMobile } from '@ff-os/shared-react/hooks/useMobile';
import { maximumImageSize } from '@ff-os/shared-utils/constants/maximum-file-sizes';
import { Area } from '@ff-os/shared-utils/types/Area';
import { isAiStudioEnabledByVariableId, selectAiStudioAppIdByVariableId, selectNameByVariableId } from '@ff-scene-plugins/gift-redux/slices/variablesSlice';
import { GiftPdpDispatch, GiftPdpState } from '@ff-scene-plugins/gift-redux/store';
import { UploadStatus } from '@gift-variable-plugins/photo-shared/PhotoVariableInput';
import classNames from 'classnames';
import { Size } from 'ff-canvas';
import { selectClipById, selectCropBoxById, selectFileNameById, selectFilterById, selectImageSizeById, selectStatusById, updateCropBox, updateFilter } from '../_redux';
import { createGiftUploadMethods } from './createGiftUploadMethods';
import { uploadImage } from './helpers/uploadImage';
import { PhotoEditPreview } from './preview/PhotoEditPreview';
import { UploadPreview } from './preview/UploadPreview';
import styles from './PhotoUploaderInputControl.module.scss';

export const _PhotoInputInline = ({
    variableIds, name,
    imageSrc, fileName, onChange,
    hasFile, fileErrors,
    hasWhiteEdges,
    imageSize, activeCropBox,
    applyCropBox, clip,
    filter, updateFilter,
    enableAiStudio,
    aiStudioProduct, aiStudioStartupAppId,
    pluginId, telemetryCategory
}: Props) => {

    const { t } = useTranslation();
    const isMobile = useMobile();
    const [isOpen, setIsOpen] = useState(true);
    const [isCropModalOpen, setIsCropModalOpen] = useState(false);
    const [isUploadMethodsWindowOpen, setIsUploadMethodsWindowOpen] = useState(false);
    const [isAiStudioOpen, setIsAiStudioOpen] = useState(false);

    const [isDragging, setIsDragging] = useState(false);

    const fileUploaderRef = createFileUploaderRef();

    const title = imageSrc
        ? t('image_uploader.title_replace')
        : t(name);

    const giftUploadMethods = useMemo(() => {
        return createGiftUploadMethods({
            dropzone: { onChange: onChange, additionalErrors: fileErrors },
            externalUpload: { variableIds: variableIds }
        }, isMobile);
    }, [fileErrors, onChange, variableIds, isMobile]);

    const showLoader = hasFile && !imageSrc;

    return (
        <Accordion>
            <AccordionField onClick={() => setIsOpen(!isOpen)} active={isOpen}>
                <Icon type='option-image' className={styles.icon} />
                {title}
            </AccordionField>

            <AccordionMenu active={isOpen} maxHeight='none' className={styles.accordion}>
                <DropArea ref={fileUploaderRef}
                    onChange={onChange}
                    maxSize={maximumImageSize}
                    onDragOver={() => setIsDragging(true)}
                    onDragLeave={() => setIsDragging(false)}>

                    {showLoader && (
                        <AspectRatio width={4} height={1}>
                            <Spinner overlaySize='full' spinnerSize='small' />
                        </AspectRatio>
                    )}

                    {!imageSrc && (
                        <div className={classNames(styles.button, { [styles.dragging]: isDragging })}
                            onClick={() => aiStudioProduct
                                ? setIsAiStudioOpen(true)
                                : setIsUploadMethodsWindowOpen(true)}>
                            <Icon type='image-file-add' size='large' />

                            {aiStudioProduct && (
                                <AiStudioButton />
                            )}
                        </div>
                    )}

                    {imageSrc && (
                        <div className={styles.preview}>
                            <UploadPreview
                                url={imageSrc}
                                variableIds={variableIds}
                                replace={() => aiStudioProduct
                                    ? setIsAiStudioOpen(true)
                                    : setIsUploadMethodsWindowOpen(true)}
                                crop={() => setIsCropModalOpen(true)} />
                        </div>
                    )}

                    {isAiStudioOpen &&  (
                        <WindowStackItem>
                            <AiStudioWindow
                                pluginId={pluginId}
                                aiStudioStartupAppId={aiStudioStartupAppId}
                                telemetryCategory={telemetryCategory}
                                uploadFiles={(files: File[]) => onChange(files)}
                                onClose={() => setIsAiStudioOpen(false)} />
                        </WindowStackItem>
                    )}

                    <UploadMethodsWindow
                        methods={giftUploadMethods}
                        isOpen={isUploadMethodsWindowOpen}
                        close={() => setIsUploadMethodsWindowOpen(false)} />

                    {imageSrc && (
                        <PhotoEditModal
                            isOpen={isCropModalOpen}
                            close={() => setIsCropModalOpen(false)}
                            clip={clip}
                            activeCropBox={activeCropBox}
                            imageSize={imageSize}
                            applyCropBox={applyCropBox}
                            filter={filter}
                            updateFilter={updateFilter}
                            overlayUrl={imageSrc}
                            enableAiStudio={enableAiStudio}
                            fileName={fileName}
                            uploadFiles={onChange}
                            pluginId={pluginId}
                            aiStudioStartupAppId={aiStudioStartupAppId}
                            telemetryCategory={telemetryCategory}>
                            <AspectRatio width={imageSize.width} height={imageSize.height}>
                                <PhotoEditPreview variableIds={variableIds} imageSize={imageSize} />
                            </AspectRatio>
                        </PhotoEditModal>
                    )}

                    {hasWhiteEdges && (
                        <Alert className={styles.alert} type='info' icon='alert-triangle-exclamation'>
                            {t('image_uploader.has_white_edges')}
                        </Alert>
                    )}
                </DropArea>
            </AccordionMenu>
        </Accordion>
    );
};

type Props = {

    variableIds: string[];

    name: string;

    status: UploadStatus | undefined;
    uploadImage: (image: File) => void;
    hasFile: boolean;

    imageSrc: string | undefined;
    fileName?: string;
    imageSize: Size;

    activeCropBox: Area;
    applyCropBox: (cropBox: Area) => void;
    clip: Clip | undefined;

    filter: string | null;
    updateFilter: (filter: string | null) => void;

    fileErrors: UploadError[];

    hasWhiteEdges: boolean;

    onChange: (files: File[]) => void;

    onConfirm?: () => void;

    enableAiStudio: boolean;
    aiStudioProduct?: boolean;
    aiStudioStartupAppId?: string;

    pluginId: string;
    telemetryCategory: string;
};

const mapStateToProps = (state: GiftPdpState, ownProps: { variableIds: string[] }) => ({
    status: selectStatusById(state, ownProps.variableIds[0]),
    name: selectNameByVariableId(state, ownProps.variableIds[0])!,
    imageSize: selectImageSizeById(state, ownProps.variableIds[0])!,
    activeCropBox: selectCropBoxById(state, ownProps.variableIds[0])!,
    clip: selectClipById(state, ownProps.variableIds[0]),
    filter: selectFilterById(state, ownProps.variableIds[0]) || null,
    fileName: selectFileNameById(state, ownProps.variableIds[0]),
    enableAiStudio: isAiStudioEnabledByTenant(state),
    aiStudioProduct: isAiStudioEnabledByVariableId(state, ownProps.variableIds[0]),
    pluginId: state.scene.pluginId,
    telemetryCategory: state.scene.options.telemetryCategory,
    aiStudioStartupAppId: selectAiStudioAppIdByVariableId(state, ownProps.variableIds[0])
});

const mapDispatchToProps = (dispatch: GiftPdpDispatch, ownProps: { variableIds: string[] }) => ({
    uploadImage: (image: File) => dispatch(uploadImage(image, ownProps.variableIds[0])),
    applyCropBox: (cropBox: Area) => dispatch(updateCropBox(ownProps.variableIds, cropBox)),
    updateFilter: (filter: string | null) => dispatch(updateFilter(ownProps.variableIds, filter))
});

export const PhotoInputInline = connect(
    mapStateToProps,
    mapDispatchToProps
)(_PhotoInputInline);